/**
 * clientVault.js — Client-sealed capsule v2.
 *
 * Garanzia: nessun contenuto in chiaro né chiave utilizzabile dalla capsula
 * lascia mai il dispositivo durante le 24h di ripensamento. Dopo T+24h il
 * contenuto esce dal client solo come:
 *   - sealed_bundle: ciphertext AES-GCM dei content
 *   - drand_ciphertext: la DEK AES-GCM tlock-encrypted sul round target
 * Il server NON possiede alcuna chiave che consenta la decifratura.
 *
 * tlock cryptographic time-lock: il drand network produce la firma BLS del
 * round target solo a `genesis + round * period`. Senza quella firma la
 * decryption (bilinear pairing BLS12-381) è impossibile.
 */
import { get, set, del, createStore } from "idb-keyval";
import { timelockEncrypt, timelockDecrypt, roundAt, Buffer } from "tlock-js";
import { HttpChainClient, HttpCachingChain } from "drand-client";

const DB_STORE = createStore("tc-vault", "capsules");

// Default drand chain: "quicknet" (fast, 3s rounds, unchained BLS signatures).
// chainHash deterministico. Verificato via /info endpoint al primo uso.
const DRAND_API_URLS = [
  "https://api.drand.sh",
  "https://api2.drand.sh",
  "https://api3.drand.sh",
  "https://drand.cloudflare.com",
];
const DRAND_QUICKNET_CHAIN_HASH =
  "52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971";

async function _chainClient() {
  // HttpCachingChain cache /info; HttpChainClient fetches signatures.
  const chainUrl = `${DRAND_API_URLS[0]}/${DRAND_QUICKNET_CHAIN_HASH}`;
  const chain = new HttpCachingChain(chainUrl);
  return new HttpChainClient(chain);
}

async function _b64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
async function _unb64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

async function _sha256Hex(buf) {
  const h = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(h))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Inizializza il record locale per una capsula GIÀ creata lato server.
 * Il server detiene solo i metadati (title, category, destinatari). Nessun
 * contenuto o chiave transita. Chiamata subito dopo POST /capsules.
 */
export async function initLocalCapsule(serverCapId, { title, category, open_at }) {
  const existing = await get(serverCapId, DB_STORE);
  if (existing && !existing.submitted) {
    // già inizializzata: aggiorna solo titolo/open_at se cambiati
    existing.title = title ?? existing.title;
    existing.category = category ?? existing.category;
    if (open_at) existing.open_at = open_at;
    await set(serverCapId, existing, DB_STORE);
    return { id: serverCapId, windowEndsAt: existing.windowEndsAt };
  }
  const dek = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const now = Date.now();
  // windowEndsAt viene definito al momento dello start-rethink.
  const record = {
    serverCapId,
    title: title || "Capsula",
    category: category || "personalizzata",
    open_at: open_at || null,
    createdAt: now,
    windowEndsAt: null,
    contents: [],
    finalMessage: null, // {iv, ciphertext}
    submitted: false,
    dek,
  };
  await set(serverCapId, record, DB_STORE);
  return { id: serverCapId, windowEndsAt: null };
}

/** Imposta la finestra 24h e open_at (chiamata dopo /start-rethink). */
export async function markRethinkStarted(localId, { open_at, windowEndsAt }) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  if (open_at) rec.open_at = open_at;
  rec.windowEndsAt = windowEndsAt;
  await set(localId, rec, DB_STORE);
  return rec;
}

/** Aggiorna metadati locali (es. open_at anticipato in Step 3). */
export async function setLocalMeta(localId, patch) {
  const rec = await get(localId, DB_STORE);
  if (!rec) return null;
  Object.assign(rec, patch);
  await set(localId, rec, DB_STORE);
  return rec;
}

/** Rimuove un contenuto locale. */
export async function removeLocalContent(localId, contentId) {
  const rec = await get(localId, DB_STORE);
  if (!rec) return;
  rec.contents = (rec.contents || []).filter((c) => c.id !== contentId);
  await set(localId, rec, DB_STORE);
}

/** Imposta/aggiorna il messaggio finale, cifrato con la DEK locale. */
export async function setLocalFinalMessage(localId, text) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  if (!text || !text.trim()) {
    rec.finalMessage = null;
    await set(localId, rec, DB_STORE);
    return;
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    rec.dek,
    new TextEncoder().encode(text)
  );
  rec.finalMessage = { iv: iv.buffer, ciphertext };
  await set(localId, rec, DB_STORE);
}

/** Decifra in memoria il final_message locale. */
export async function getLocalFinalMessage(localId) {
  const rec = await get(localId, DB_STORE);
  if (!rec || !rec.finalMessage) return "";
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(rec.finalMessage.iv) },
    rec.dek,
    rec.finalMessage.ciphertext
  );
  return new TextDecoder().decode(pt);
}

/**
 * Crea una capsula locale. Il server riceve solo i metadati (title/category)
 * via POST /capsules — NON riceve contenuti né chiavi. Ritorna il server
 * capsule_id (per associare l'ancoraggio futuro) e un locale "unlockWindow".
 */
export async function createLocalCapsule(api, { title, category, open_at }) {
  // 1) Crea entry server (solo metadati)
  const { data } = await api.post("/capsules", {
    title: title || "Capsula",
    category: category || "personalizzata",
  });
  const serverCapId = data.id;

  // 2) Genera DEK locale NON-estraibile (WebCrypto).
  const dek = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true, // extractable=true: necessario SOLO per il tlock-wrap finale a T+24h.
    ["encrypt", "decrypt"]
  );

  const now = Date.now();
  const windowEndsAt = now + 24 * 60 * 60 * 1000;
  const record = {
    serverCapId,
    title,
    category,
    open_at, // ISO string (data di apertura futura, dopo T+24h)
    createdAt: now,
    windowEndsAt,
    contents: [], // {id, kind, title, iv, ciphertext (ArrayBuffer)}
    recipients: [],
    submitted: false,
    dek, // CryptoKey object: idb-keyval serializza correttamente.
  };
  await set(serverCapId, record, DB_STORE);
  return { id: serverCapId, windowEndsAt };
}

export async function listLocalCapsules() {
  const { keys } = await import("idb-keyval");
  const ids = await keys(DB_STORE);
  const out = [];
  for (const id of ids) {
    const r = await get(id, DB_STORE);
    if (r && !r.submitted)
      out.push({
        id: r.serverCapId,
        title: r.title,
        windowEndsAt: r.windowEndsAt,
        open_at: r.open_at,
        contents_count: r.contents.length,
      });
  }
  return out;
}

export async function getLocalCapsule(localId) {
  return get(localId, DB_STORE);
}

/** Cifra immediatamente il contenuto con la DEK e salva ciphertext. */
export async function addLocalContent(localId, { kind, title, text, file }) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  if (rec.submitted) throw new Error("Capsula già inviata");

  let plaintext;
  if (kind === "text") plaintext = new TextEncoder().encode(text || "");
  else if (file) plaintext = await file.arrayBuffer();
  else throw new Error("Contenuto mancante");

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    rec.dek,
    plaintext
  );
  const cid = crypto.randomUUID();
  rec.contents.push({
    id: cid,
    kind,
    title: title || "",
    iv: iv.buffer,
    ciphertext,
    size: ciphertext.byteLength,
    mime: file?.type || "text/plain",
    filename: file?.name,
  });
  await set(localId, rec, DB_STORE);
  return cid;
}

/** Decifra per anteprima in memoria SENZA persistere plaintext. */
export async function previewLocalContent(localId, contentId) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  const c = rec.contents.find((x) => x.id === contentId);
  if (!c) throw new Error("Contenuto non trovato");
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(c.iv) },
    rec.dek,
    c.ciphertext
  );
  if (c.kind === "text") return { kind: "text", text: new TextDecoder().decode(pt) };
  return { kind: c.kind, blob: new Blob([pt], { type: c.mime || "application/octet-stream" }) };
}

/** Rimuove tutte le tracce locali. Non tocca file originali dell'utente. */
export async function revokeLocalCapsule(localId) {
  await del(localId, DB_STORE);
}

export async function setLocalRecipients(localId, recipients) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  rec.recipients = recipients;
  await set(localId, rec, DB_STORE);
}

/**
 * Sigilla la capsula e la invia al server. Nel flusso D viene chiamata
 * IMMEDIATAMENTE al momento del lancio (click "Lancia nel futuro" dal
 * wizard): il server riceve subito il bundle cifrato (opaco) e lo mette
 * in RIPENSAMENTO_24H senza alcun ancoraggio on-chain. L'anchor avviene
 * a T+24h via background ticker. Durante la finestra 24h l'owner puo'
 * revocare: il server purga il bundle, zero gas, zero traccia on-chain.
 *
 * Passi:
 * 1. Esporta la DEK in bytes (brevemente in memoria).
 * 2. drand_round = roundAt(open_at).
 * 3. tlock-encrypt DEK -> drand_ciphertext.
 * 4. Costruisci sealed_bundle = JSON serializzato dei content ciphertext + iv.
 * 5. POST /capsules/{id}/submit-sealed.
 * 6. Elimina il record locale IndexedDB: la capsula ora e' interamente
 *    rappresentata lato server dal solo bundle opaco.
 */
export async function sealAndSubmit(api, localId) {
  const rec = await get(localId, DB_STORE);
  if (!rec) throw new Error("Capsula locale non trovata");
  if (rec.submitted) return { already: true };
  if (!rec.open_at)
    throw new Error("Data di apertura non impostata");

  // 1) Export DEK as raw bytes
  const dekRaw = await crypto.subtle.exportKey("raw", rec.dek);

  // 2) Build sealed_bundle: struttura opaca per il server
  const bundleObj = {
    version: 2,
    contents: rec.contents.map((c) => ({
      id: c.id,
      kind: c.kind,
      title: c.title,
      iv_b64: _b64Sync(c.iv),
      ciphertext_b64: _b64Sync(c.ciphertext),
      mime: c.mime,
      filename: c.filename,
    })),
    final_message: rec.finalMessage
      ? {
          iv_b64: _b64Sync(rec.finalMessage.iv),
          ciphertext_b64: _b64Sync(rec.finalMessage.ciphertext),
        }
      : null,
  };
  const bundleBytes = new TextEncoder().encode(JSON.stringify(bundleObj));

  // 3) drand round calculation
  const client = await _chainClient();
  const chainInfo = await client.chain().info();
  const openAtMs = new Date(rec.open_at).getTime();
  const round = roundAt(openAtMs, chainInfo); // tlock-js helper

  // 4) tlock-encrypt the DEK (hybrid: tlock wraps a KEM key, encapsulates DEK)
  const drandCt = await timelockEncrypt(round, Buffer.from(dekRaw), client);

  // 5) Submit
  const hash = await _sha256Hex(bundleBytes);
  const payload = {
    sealed_bundle_b64: _b64Sync(bundleBytes),
    drand_ciphertext_b64: _b64Sync(new TextEncoder().encode(drandCt)),
    drand_chain: DRAND_QUICKNET_CHAIN_HASH,
    drand_round: round,
    encrypted_content_hash: hash,
    open_at: rec.open_at,
    bundle_summary: {
      count: rec.contents.length,
      kinds: rec.contents.map((c) => c.kind),
    },
  };
  const { data } = await api.post(`/capsules/${rec.serverCapId}/submit-sealed`, payload);

  // 6) Elimina il record locale: ora il bundle vive sul server (cifrato).
  //    Non c'e' piu' bisogno della DEK locale ne' dei contents ciphertext.
  await del(localId, DB_STORE);
  return { ok: true, capsule: data };
}

function _b64Sync(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/**
 * Apertura client-side al momento della scadenza.
 * Nessuna decifratura server-side.
 */
export async function openSealedCapsule(api, serverCapId, bundlePath) {
  const { data } = await api.get(bundlePath || `/capsules/${serverCapId}/sealed-bundle`);
  // Decrypt DEK via drand signature at target round
  const client = await _chainClient();
  const drandCtBytes = await _unb64(data.drand_ciphertext_b64);
  const drandCtStr = new TextDecoder().decode(drandCtBytes);
  const dekRaw = await timelockDecrypt(drandCtStr, client); // throws se round non ancora firmato
  const dek = await crypto.subtle.importKey(
    "raw",
    dekRaw,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );
  // Decrypt bundle
  const bundleBytes = await _unb64(data.sealed_bundle_b64);
  const bundle = JSON.parse(new TextDecoder().decode(bundleBytes));
  const results = [];
  for (const c of bundle.contents) {
    const iv = new Uint8Array(await _unb64(c.iv_b64));
    const ct = await _unb64(c.ciphertext_b64);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, dek, ct);
    results.push({
      id: c.id,
      kind: c.kind,
      title: c.title,
      ...(c.kind === "text"
        ? { text: new TextDecoder().decode(pt) }
        : { blob: new Blob([pt], { type: c.mime || "application/octet-stream" }),
            filename: c.filename }),
    });
  }
  let finalMessage = "";
  if (bundle.final_message) {
    const iv = new Uint8Array(await _unb64(bundle.final_message.iv_b64));
    const ct = await _unb64(bundle.final_message.ciphertext_b64);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, dek, ct);
    finalMessage = new TextDecoder().decode(pt);
  }
  return { capsule_id: data.capsule_id, open_at: data.open_at, contents: results, final_message: finalMessage };
}

/** @deprecated Flusso D: `sealAndSubmit` ora viene chiamato immediatamente al
 * momento del lancio dal Wizard, non piu' in differita. Questa funzione resta
 * come no-op per compatibilita' con qualsiasi caller legacy. */
export async function processExpiredLocal(_api) {
  return [];
}
